<script>
  import { onMount } from 'svelte';
  import { scaleBand, scaleLinear } from 'd3-scale';
  import { max, sum } from 'd3-array';

  export let data;

  // $: data = jsonData?.histogram || [];
  $: totalSum = sum(data, d => d["Antal rader"]);

  let width = 300;
  let height = 400;
  let padding = { top: 40, right: 20, bottom: 60, left: 40 };

  let selectedBar = null;
  let hoveredBar = null;

  $: xScale = scaleBand()
    .domain(data.map(d => d.intervall))
    .range([padding.left, width - padding.right])
    .padding(0.1);

  $: yScale = scaleLinear()
    .domain([0, max(data, d => d["Antal rader"]) || 0])
    .range([height - padding.bottom, padding.top]);

  function getPercentage(value) {
    return ((value / totalSum) * 100).toFixed(1);
  }

  function handleMouseEnter(d) {
    hoveredBar = d;
  }

  function handleMouseLeave() {
    hoveredBar = null;
  }

  function handleClick(d) {
    if (selectedBar === d) {
      selectedBar = null;
    } else {
      selectedBar = d;
    }
  }

  function handleTouchStart(event, d) {
    event.preventDefault(); // Prevent mouse events from firing
    handleClick(d);
  }

  onMount(() => {
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        width = entry.contentRect.width;
      }
    });

    resizeObserver.observe(document.querySelector('.chart-container'));

    return () => {
      resizeObserver.disconnect();
    };
  });
</script>

<div class="chart-container">
  {#if !data}
    <p>Waiting for data...</p>
  {:else if data.length === 0}
    <p>No histogram data available.</p>
  {:else}
    <svg {width} {height}>
      {#each data as d}
        <g 
          on:mouseenter={() => handleMouseEnter(d)}
          on:mouseleave={handleMouseLeave}
          on:click={() => handleClick(d)}
          on:touchstart={(e) => handleTouchStart(e, d)}
        >
          <rect
            x={xScale(d.intervall)}
            y={yScale(d["Antal rader"])}
            width={xScale.bandwidth()}
            height={height - padding.bottom - yScale(d["Antal rader"])}
            fill={selectedBar === d ? "var(--themeColor1)" : hoveredBar === d ? "var(--themeColor2)" : "var(--themeColor1)"}
          />
          {#if selectedBar === d || hoveredBar === d}
            <text
              x={xScale(d.intervall) + xScale.bandwidth() / 2}
              y={yScale(d["Antal rader"]) - 19}
              text-anchor="middle"
              fill="var(--themeColor1)"
              font-weight="bold"
            >
              <tspan x={xScale(d.intervall) + xScale.bandwidth() / 2} dy="0">{d["Antal rader"]}</tspan>
              <tspan x={xScale(d.intervall) + xScale.bandwidth() / 2} dy="1.2em">({getPercentage(d["Antal rader"])}%)</tspan>
            </text>
          {/if}
        </g>
      {/each}
      
      <!-- X-axis -->
      <g transform="translate(0,{height - padding.bottom - 5})">
        {#each data as d}
          <text
            x={xScale(d.intervall) + xScale.bandwidth() / 2}
            y="20"
            text-anchor="end"
            transform="rotate(-45 {xScale(d.intervall) + xScale.bandwidth() / 2}, 20)"
          >
            {d.intervall}
          </text>
        {/each}
      </g>
      
      <!-- Y-axis -->
      <g transform="translate({padding.left}, 0)">
        {#each yScale.ticks(5) as tick}
          <g transform="translate(0, {yScale(tick)})">
            <line x2="-6" stroke="currentColor" />
            <text x="-10" y="4" text-anchor="end">{tick}</text>
          </g>
        {/each}
      </g>
    </svg>
  {/if}
</div>

<style>
  .chart-container {
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
  }
  
  svg {
    width: 100%;
    height: auto;
  }

  text {
    font-size: 12px;
    font-family: sans-serif;

  }

  rect {
    transition: fill 0.2s;
  }
</style>